/* ===========================================================
   panels/Compare.jsx — side by side, changes highlighted

   Left: before. Right: after. Removed words are struck in red on the
   left, added words green on the right; whole new or removed
   paragraphs get a coloured bar. "Only show changes" hides the rest.
   =========================================================== */

import { useEffect, useMemo, useState } from 'preact/hooks';
import { Dialog, Button, openModal } from '../ui/ui.jsx';
import { diffDocs } from '../../shared/diff.js';
import { renderHTML } from '../../shared/render.js';
import { loadKatex, mathHTML } from '../lib/katex.js';
import { mode } from '../state.js';

export function Html({ node, class: cls = '' }) {
  return <div class={`prose static ${cls}`} dangerouslySetInnerHTML={{ __html: renderHTML(node, { math: mathHTML }) }} />;
}

function CompareBox({ left, right, leftLabel, rightLabel, done, extra }) {
  const [onlyChanges, setOnly] = useState(false);
  const [, setReady] = useState(0);
  useEffect(() => { loadKatex().then(() => setReady(1)).catch(() => {}); }, []);
  const d = useMemo(() => diffDocs(left, right), [left, right]);
  const rows = onlyChanges ? d.rows.filter(r => r.kind !== 'same') : d.rows;
  const s = d.stats;
  const summary = d.same ? 'No differences.'
    : [s.changed && `${s.changed} changed`, s.added && `${s.added} added`, s.removed && `${s.removed} removed`].filter(Boolean).join(', ')
      + (mode.value === 'dev' ? ' (blocks)' : ' paragraphs or blocks');
  return (
    <Dialog title={mode.value === 'dev' ? 'Diff' : 'Compare'} full onClose={() => done(null)}
      actions={<>{extra}<span class="grow" /><Button onClick={() => done(null)}>Close</Button></>}>
      <div class="compare-bar">
        <span class="compare-summary" role="status">{summary}</span>
        <label class="check"><input type="checkbox" checked={onlyChanges} onChange={e => setOnly(e.target.checked)} /><span>Only show changes</span></label>
      </div>
      <div class="compare">
        <div class="compare-col"><h3>{leftLabel}</h3></div>
        <div class="compare-col"><h3>{rightLabel}</h3></div>
        {rows.map((r, i) => (
          <>
            <div class={`compare-cell k-${r.kind}`} key={`l${i}`}>{r.left ? <Html node={{ type: 'doc', content: [r.left] }} /> : <div class="compare-gap" aria-label="nothing here before" />}</div>
            <div class={`compare-cell k-${r.kind}`} key={`r${i}`}>{r.right ? <Html node={{ type: 'doc', content: [r.right] }} /> : <div class="compare-gap" aria-label="removed" />}</div>
          </>
        ))}
      </div>
    </Dialog>
  );
}

/** Open the compare view. extra = more footer buttons (e.g. Restore). */
export const compareView = opts => openModal(done => <CompareBox {...opts} done={done} />);
